import { observer } from 'mobx-react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import AssignmentIcon from '@material-ui/icons/Assignment';
import FolderIcon from '@material-ui/icons/Folder';
import Timeline from '@material-ui/icons/Timeline';
import TuneOutlinedIcon from '@material-ui/icons/TuneOutlined';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import DashboardIcon from '@material-ui/icons/Dashboard';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import MoneyIcon from '@material-ui/icons/Money';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuIcon from '@material-ui/icons/Menu';
import PersonIcon from '@material-ui/icons/Person';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import SwapHorizOutlinedIcon from '@material-ui/icons/SwapHorizOutlined';
import InputIcon from '@material-ui/icons/Input';
import ScreenShareIcon from '@material-ui/icons/ScreenShare';
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import BuildIcon from '@material-ui/icons/Build';
import PowerSettingsIcon from '@material-ui/icons/PowerSettingsNew';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import AppsIcon from '@material-ui/icons/Apps';
import SettingsIcon from '@material-ui/icons/Settings';
import CloseIcon from '@material-ui/icons/Close';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import InfoIcon from '@material-ui/icons/Info';

import Tooltip from 'components/Tooltip/Tooltip';
import compose from 'helpers/compose';
import AdvisorInfo from 'components/AdvisorInfo/AdvisorInfo';

import useStyles from './styles';
import { DetailPage, Flows } from 'pages/Accounts/Detail/enums';
import { RootStore } from 'stores/rootStore';
import { EdwardColors, AccountStatus, Client, ActiveStatus, TransferOption } from 'enums';
import { Permission } from 'helpers/permissions';

enum Widths {
  MOBILE = 0,
  TABLET = 1,
  DESKTOP = 2,
}

type Props = {
  rootStore: RootStore;
  header?: boolean;
  insite?: boolean;
};

const Sidebar: React.FC<Props> = ({ rootStore, header, insite }) => {
  const { t } = useTranslation();
  const { userStore } = rootStore.stores;
  const userMode = userStore.checkPermissions(Permission.APP_ENDUSER);
  const classes = useStyles({ header, insite, userMode });
  const advisorData = userStore.advisorData();
  const {
    loaded,
    handlePageChange,
    activeFlow,
    activePage,
    handleEdit,
    status,
    isEditDisabled,
    editMode,
    //activeSection, - FIXME: DRF remove from uiStore
    openInfoModal,
    openDepositModal,
    openClientViewModal,
    questionnaireOverdue,
    processingChange,
    locked,
    activeStatus,
    transferOption,
    profileLockActive
  } = rootStore.stores.uiStore.userMenuData;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.only('sm'), { noSsr: true });
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), { noSsr: true });

  const width = desktop ? 2 : tablet ? 1 : 0;

  // FIXME: conditions
  const signature =
    userStore.activeAccount?.status === AccountStatus.TO_BE_SIGNED ||
    userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN;
  const signatureDisabled =
    !signature &&
    (editMode || status !== AccountStatus.TO_BE_SIGNED) &&
    !userStore.activeAccount?.pendingSignature &&
    !userStore.activeAccount?.pendingRequestSignature;
  const activeSignature =
    userStore.activeAccount?.status === AccountStatus.TO_BE_SIGNED ||
    userStore.activeAccount?.pendingSignature ||
    userStore.activeAccount?.pendingRequestSignature;

  const sidebar = [
    {
      label: 'general_show',
      title: true,
      divider: false,
      permission: Permission.APP_ADVISOR,
      icon: <AppsIcon />,
      advisorOrder: 1,
      userOrder: 0,
    },
    {
      page: DetailPage.DASHBOARD,
      label: 'accountDetail_dashboard',
      icon: <DashboardIcon />,
      disabled: signature || processingChange,
      advisorOrder: 2,
      userOrder: 1,
    },
    {
      page: DetailPage.HISTORY,
      label: 'accountDetail_history',
      shortLabel: 'accountDetail_historyShort',
      icon: <Timeline />,
      disabled: signature || processingChange,
      advisorOrder: 3,
      userOrder: 2,
    },
    {
      page: DetailPage.MOVEMENTS,
      label: 'accountDetail_movements',
      icon: <SwapHorizOutlinedIcon />,
      disabled: signature || processingChange,
      advisorOrder: 4,
      userOrder: 0,
    },
    {
      page: DetailPage.REPORTS,
      label: 'accountDetail_reportsAndSummaries',
      icon: <AssessmentOutlinedIcon />,
      disabled: signature || processingChange,
      advisorOrder: 5,
      userOrder: 5,
    },
    {
      page: DetailPage.SIGNED_DOCUMENTS,
      label: 'accountDetail_signedDocuments',
      icon: <FolderIcon />,
      disabled: signature || processingChange,
      advisorOrder: 6,
      userOrder: 7,
    },
    {
      label: 'general_set',
      title: true,
      permission: Permission.APP_ADVISOR,
      icon: <SettingsIcon />,
      advisorOrder: 7,
      userOrder: 0,
    },
    {
      page: DetailPage.PLAN,
      flow: [Flows.PLAN],
      label: 'accountDetail_investmentPlan',
      icon: <TuneOutlinedIcon />,
      disabled: signature || processingChange,
      advisorOrder: 8,
      userOrder: 3,
    },
    {
      page: DetailPage.ID,
      flow: [Flows.PLAN],
      label: 'accountDetail_investmentQuestionnaire',
      icon: <AssignmentIcon />,
      disabled: signature || processingChange || profileLockActive,
      // TODO
      notification:
        questionnaireOverdue && userStore.checkPermissions(Permission.APP_ADVISOR)
          ? 'accounts_questionnaireOverdue'
          : undefined,
      tooltip: profileLockActive ? 'accountDetail_questionnaire_locked' : undefined,
      advisorOrder: 9,
      userOrder: 4,
    },
    {
      page: activeFlow ? DetailPage.SINGLE_FEES : DetailPage.FEES,
      altPage: DetailPage.REGULAR_FEES,
      flow: [Flows.PRICELIST, Flows.PLAN],
      label: 'accountDetail_fees',
      icon: <MonetizationOnIcon />,
      disabled: signature || processingChange,
      //flow: activeSection === DetailSection.PLAN,
      advisorOrder: 10,
      userOrder: 0,
    },
    {
      page: activeFlow ? DetailPage.PERSONAL_DATA : DetailPage.PROFILE,
      flow: [Flows.PERSONAL],
      label: 'clientProfile_profile',
      icon: <AccountCircleIcon />,
      disabled: signature || processingChange,
      advisorOrder: 11,
      userOrder: 0,
    },
    {
      //section: status === AccountStatus.ACTIVE ? DetailSection.WITHDRAW : DetailSection.CLOSE_ACCOUNT,
      page: DetailPage.WITHDRAW,
      label: status === AccountStatus.ACTIVE ? 'accountDetail_withdraw' : 'accountDetail_closeAccount',
      icon: status === AccountStatus.ACTIVE ? <MoneyIcon /> : <CancelPresentationIcon />,
      flow: status === AccountStatus.ACTIVE ? [Flows.WITHDRAWAL] : [Flows.CLOSE_ACCOUNT],
      startFlow: !activeFlow,
      disabled:
        //(isEditDisabled && status === AccountStatus.ACTIVE && !locked) ||
        transferOption === TransferOption.DISABLED_TRANSFER ||
        (status === AccountStatus.ACTIVE && isEditDisabled && activeFlow?.id !== Flows.WITHDRAWAL && !locked) ||
        (locked && activeStatus !== ActiveStatus.IN_LIQUIDATION) ||
        //(editMode && status === AccountStatus.AWAITING_APPROVAL) ||
        signature ||
        processingChange,
      tooltip:
        transferOption === TransferOption.DISABLED_TRANSFER
          ? `accountDetail_transfer_option_${transferOption}`
          : undefined,
      advisorOrder: 12,
      userOrder: 0,
    },
    {
      page: DetailPage.TRANSFER,
      label: 'accountDetail_transfer',
      icon: <DoubleArrowIcon />,
      flow: [Flows.TRANSFER],
      startFlow: !activeFlow,
      disabled: processingChange || locked || transferOption !== TransferOption.ENABLED,
      tooltip: [TransferOption.ENABLED, TransferOption.PENDING, undefined].includes(transferOption)
        ? undefined
        : `accountDetail_transfer_option_${transferOption}`,
      advisorOrder: 13,
      userOrder: 0,
    },
    {
      page: DetailPage.DEPOSIT,
      label: 'accountDetail_depositMoney',
      icon: <InputIcon />,
      onClick: openDepositModal ? () => openDepositModal(true) : undefined,
      disabled: processingChange || locked,
      advisorOrder: 14,
      userOrder: 6,
    },
    {
      page: DetailPage.ADVISOR_INFO,
      label: 'accountDetail_advisorInfo',
      icon: <PersonIcon />,
      onClick: openInfoModal ? () => openInfoModal(true) : undefined,
      noMenu: true,
      advisorOrder: 15,
      userOrder: 8,
    },
    {
      page: DetailPage.CLIENT_VIEW,
      label: 'accountDetail_clientView',
      icon: <ScreenShareIcon />,
      onClick: openClientViewModal ? () => openClientViewModal(true) : undefined,
      //disabled: editMode,
      disabled: processingChange,
      advisorOrder: 16,
      userOrder: 0,
    },
    {
      page: DetailPage.SIGNATURE,
      label: 'accountDetail_signature',
      shortLabel: 'accountDetail_signatureShort',
      icon: <MobileFriendlyIcon />,
      disabled: signatureDisabled || processingChange,
      advisorOrder: 0,
      userOrder: 7,
      highlight: activeSignature,
    },
  ];

  const actions = [
    {
      label: 'navMenu_setupAccount',
      icon: <BuildIcon />,
      disabled: false,
      onClick: userStore.accountManagement,
      divider: true,
      hide: userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN,
    },
    {
      label: 'general_logout',
      icon: <PowerSettingsIcon />,
      disabled: false,
      onClick: userStore.logout,
    },
  ];

  const menu =
    width < Widths.TABLET
      ? []
      : sidebar
          .filter(
            (a) =>
              userStore.checkPermissions(Permission.PAGE_DETAIL, a.page) && // || a.section
              (!a.permission || userStore.checkPermissions(a.permission)) &&
              (!a.noMenu || (a.noMenu && width <= Widths.TABLET)),
          )
          .sort((a, b) => (userMode ? a.userOrder - b.userOrder : a.advisorOrder - b.advisorOrder));

  const dropdown =
    width < Widths.TABLET
      ? sidebar.filter(
          (a) =>
            userStore.checkPermissions(Permission.PAGE_DETAIL, a.page) && //|| a.section
            (!a.permission || userStore.checkPermissions(a.permission)),
        )
      : [];

  if (!loaded) return null;

  const pageNotInMenu = !sidebar.some((item) => item.page === activePage);
  const altPageMatch = sidebar.some((item) => item.altPage === activePage);

  return (
    <>
      <List
        component="nav"
        className={`${classes.root} ${rootStore.stores.uiStore.detailMenuOpen ? 'open' : 'closed'} ${
          menu.length === 0 ? classes.dropdown : ''
        }`}
      >
        {width > Widths.MOBILE && (
          <>
            <MenuItem
              onClick={(e) => {
                rootStore.stores.uiStore.setDetailMenuOpen(!rootStore.stores.uiStore.detailMenuOpen);
                e.stopPropagation();
              }}
              className={`${classes.item} ${classes.openClose}`}
            >
              <ListItemIcon className={`${classes.icon}`}>
                {rootStore.stores.uiStore.detailMenuOpen ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
              </ListItemIcon>
            </MenuItem>
          </>
        )}

        {menu.map(
          ({
            flow,
            title,
            divider,
            label,
            shortLabel,
            icon,
            page,
            altPage,
            disabled,
            onClick,
            startFlow,
            notification,
            tooltip,
            highlight,
          }) => {
            const pageMatch = (page && page === activePage) || (altPage && altPage === activePage);
            const selected =
              pageMatch || (pageNotInMenu && activeFlow && activeFlow.menuItem === page && !altPageMatch);
            const disabledByFlow = !!flow && !!activeFlow && !flow.includes(activeFlow.id);

            return (
              <Fragment key={label}>
                <Tooltip
                  title={rootStore.stores.uiStore.detailMenuOpen || title ? '' : t(label)}
                  inverted
                  placement="right"
                >
                  <MenuItem
                    disabled={disabled || disabledByFlow}
                    onClick={() => {
                      page && !onClick && !startFlow && handlePageChange && handlePageChange(page as DetailPage);
                      //flow && editMode && changeFlowTab && changeFlowTab(tab as DetailTab);
                      startFlow && handleEdit && !!flow && handleEdit(flow[0] as Flows);
                      onClick && onClick();
                    }}
                    className={`${title ? classes.title : classes.item} ${
                      divider === false ? classes.titleNoDivider : ''
                    } ${!rootStore.stores.uiStore.detailMenuOpen && title ? classes.titleClosed : ''} ${
                      selected ? classes.selected : ''
                    } ${(!!flow && !!activeFlow && !disabledByFlow) || highlight ? classes.activeFlow : ''}`}
                  >
                    <ListItemIcon className={classes.icon}>{icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <>
                          {t(tablet && shortLabel ? shortLabel : label)}
                          {/* FIXME: c&p */}
                          {(notification || tooltip) && (
                            <Tooltip
                              title={t(notification || tooltip || '')}
                              style={{ pointerEvents: 'auto' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>
                                {notification && <NotificationImportantIcon htmlColor={EdwardColors.ALERT} />}
                                {tooltip && <InfoIcon htmlColor={EdwardColors.GRAY_MEDIUM} />}
                              </span>
                            </Tooltip>
                          )}
                        </>
                      }
                    />
                  </MenuItem>
                </Tooltip>
              </Fragment>
            );
          },
        )}
        {dropdown.length > 0 && (
          <MenuItem
            onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
            className={`${classes.item}`}
          >
            <ListItemIcon className={classes.icon}>
              <MenuIcon />
            </ListItemIcon>
          </MenuItem>
        )}
      </List>
      {dropdown.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          keepMounted
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          getContentAnchorEl={null}
          className={classes.userMenu}
          transitionDuration={0}
        >
          <MenuItem key="close" className={classes.close}>
            <ListItemIcon
              onClick={() => {
                setAnchorEl(null);
              }}
            >
              <CloseIcon />
            </ListItemIcon>
          </MenuItem>

          {dropdown.map(
            ({
              flow,
              title,
              label,
              icon,
              page,
              altPage,
              disabled,
              onClick,
              startFlow,
              notification,
              tooltip,
              highlight,
            }) => {
              const pageMatch = (page && page === activePage) || (altPage && altPage === activePage);
              const selected =
                pageMatch || (pageNotInMenu && activeFlow && activeFlow.defaultPage === page && !altPageMatch);
              const disabledByFlow = !!flow && !!activeFlow && !flow.includes(activeFlow.id);

              return (
                <MenuItem
                  key={label}
                  disabled={disabled || disabledByFlow}
                  onClick={() => {
                    page && !onClick && !startFlow && handlePageChange && handlePageChange(page as DetailPage);
                    //flow && editMode && changeFlowTab && changeFlowTab(tab as DetailTab);
                    //onClick && onClick(section as DetailSection);
                    startFlow && handleEdit && !!flow && handleEdit(flow[0] as Flows);
                    onClick && onClick();

                    setAnchorEl(null);
                  }}
                  className={`${title ? classes.title : classes.userMenuItem} ${selected ? classes.selected : ''} ${
                    (!!flow && !!activeFlow && !disabledByFlow) || highlight ? classes.activeFlow : ''
                  }`}
                >
                  {!title && <ListItemIcon>{icon}</ListItemIcon>}
                  <ListItemText>{t(label)}</ListItemText>
                  {(notification || tooltip) && (
                    <Tooltip
                      title={t(notification || tooltip || '')}
                      style={{ pointerEvents: 'auto' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>
                        {notification && <NotificationImportantIcon htmlColor={EdwardColors.ALERT} />}
                        {tooltip && <InfoIcon htmlColor={EdwardColors.GRAY_MEDIUM} />}
                      </span>
                    </Tooltip>
                  )}
                </MenuItem>
              );
            },
          )}

          {header &&
            actions.map(({ label, icon, disabled, onClick, divider, hide }) => {
              return !hide ? (
                <MenuItem
                  key={label}
                  disabled={disabled}
                  onClick={() => {
                    onClick();
                    setAnchorEl(null);
                  }}
                  className={`${classes.userMenuItem} ${divider ? classes.divider : ''}`}
                >
                  <ListItemIcon>{icon}</ListItemIcon>
                  <ListItemText>{t(label)}</ListItemText>
                </MenuItem>
              ) : null;
            })}
        </Menu>
      )}
      {advisorData && !header && !insite && (
        <Box className={`${classes.advisorInfo} ${rootStore.stores.uiStore.detailMenuOpen ? 'open' : 'closed'}`}>
          <AdvisorInfo
            advisorData={advisorData}
            short
            openInfoModal={openInfoModal}
            menuOpen={rootStore.stores.uiStore.detailMenuOpen}
          />
        </Box>
      )}
    </>
  );
};

export default compose(observer)(Sidebar);
